import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '../services/apiClient'
import * as authService from '../services/authService'
import { useUiStore } from './uiStore'

export interface User {
  id: number
  email: string
  username: string
  nickname: string
  fontSize?: number
  role: 'sysadmin' | 'group_admin' | 'user'
}

export interface UserStats {
  snapshotCount: number
  bookmarkCount: number
  noteCount: number
}

export const useUserStore = defineStore('user', () => {
  const currentUser = ref<User | null>(null)
  const csrfToken = ref<string | null>(null)
  const userStats = ref<UserStats>({
    snapshotCount: 0,
    bookmarkCount: 0,
    noteCount: 0,
  })

  const isLoggedIn = computed(() => !!currentUser.value && !!csrfToken.value)

  // 從 Cookie 恢復會話（apiClient 統一管理 CSRF token）
  const restoreSession = async () => {
    try {
      // 驗證會話並獲取用戶信息（使用 apiClient 統一認證管理）
      const data = await apiClient.getMe()

      if (data && data.user) {
        const { user, csrfToken: token } = data
        currentUser.value = user
        csrfToken.value = token || null
        // 同步字型大小（v0.3.41）
        if (user.fontSize) {
          const uiStore = useUiStore()
          uiStore.syncFromServer(user.fontSize)
        }
        // 載入用戶統計
        await fetchUserStats()
      } else {
        clearSession()
      }
    } catch (error) {
      console.error('Failed to restore session:', error)
      clearSession()
    }
  }

  // 登入
  const login = async (
    credential: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.login(credential, password)

      if (!response.success || !response.data) {
        return {
          success: false,
          error: response.error || '登入失敗',
        }
      }

      const { user, csrfToken: token } = response.data
      currentUser.value = user
      csrfToken.value = token || null

      // 同步字型大小（v0.3.41）
      if (user.fontSize) {
        const uiStore = useUiStore()
        uiStore.syncFromServer(user.fontSize)
      }

      // 載入用戶統計
      await fetchUserStats()

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '登入失敗',
      }
    }
  }

  // 註冊
  const register = async (
    email: string,
    username: string,
    nickname: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.register(email, username, nickname, password)

      if (!response.success) {
        return {
          success: false,
          error: response.error || '註冊失敗',
        }
      }

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '註冊失敗',
      }
    }
  }

  // 登出
  const logout = async (): Promise<void> => {
    if (!currentUser.value) return

    try {
      await authService.logout()
    } catch (error) {
      console.error('Failed to logout:', error)
    } finally {
      // 清除會話和虛擬桌面狀態
      clearSession()
      // 清除 sessionStorage 中的暫存 frames
      const { useFrameStore } = await import('./frameStore')
      const frameStore = useFrameStore()
      frameStore.clearFramesFromSession()
    }
  }

  // 清除會話
  const clearSession = () => {
    currentUser.value = null
    csrfToken.value = null
    userStats.value = {
      snapshotCount: 0,
      bookmarkCount: 0,
      noteCount: 0,
    }
    // 重置字型大小（v0.3.41）
    const uiStore = useUiStore()
    uiStore.reset()
  }

  // 獲取用戶統計信息
  const fetchUserStats = async (): Promise<void> => {
    if (!currentUser.value) return

    try {
      const user = await apiClient.getUserProfile(currentUser.value.id)
      if (user) {
        userStats.value = {
          snapshotCount: user.snapshotCount || 0,
          bookmarkCount: user.bookmarkCount || 0,
          noteCount: user.noteCount || 0,
        }
      }
    } catch (error) {
      console.error('Failed to fetch user stats:', error)
    }
  }

  // 設置會話（用於 Passkey 登入和恢復碼登入）
  const setSession = (user: User, token: string): void => {
    currentUser.value = user
    csrfToken.value = token
    // 同步字型大小（v0.3.41）
    if (user.fontSize) {
      const uiStore = useUiStore()
      uiStore.syncFromServer(user.fontSize)
    }
  }

  // 更新用戶信息
  const updateUserInfo = async (nickname: string): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser.value) {
      return { success: false, error: '未登入' }
    }

    try {
      const updated = await apiClient.updateUserProfile(currentUser.value.id, { nickname })

      if (updated) {
        currentUser.value = {
          ...currentUser.value,
          nickname,
        }
        return { success: true }
      }

      return { success: false, error: '更新失敗' }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '更新失敗',
      }
    }
  }

  return {
    currentUser,
    csrfToken,
    userStats,
    isLoggedIn,
    restoreSession,
    login,
    register,
    logout,
    setSession,
    fetchUserStats,
    updateUserInfo,
  }
})
